import { localeUI } from './format';
/**
 * HILOS DE EMAIL — la lógica PURA con la que el hilo del claim se lee como una conversación.
 *
 * Pedido del equipo (2026-09-02): "no se entiende dónde empieza y dónde termina un email, ni cuál
 * es respuesta de cuál". El dato para saberlo no existe en la tabla: `claim_emails` guarda el
 * Message-ID del entrante (para no duplicar) pero NO el In-Reply-To, y el Message-ID de lo que
 * mandamos por Resend nunca lo vemos. Así que se hila por el ASUNTO normalizado —"Re: 0825344625"
 * y "0825344625" son la misma conversación—, que es exactamente lo que ya hace el buzón de
 * departamento (`claveDeHilo`) y lo que hace el backend para asignarle la respuesta a quien
 * mandó el original. Una sola regla, la misma en los tres lados.
 *
 * Dentro de la conversación, un correo es RESPUESTA del anterior cuando trae "Re:" o cuando cambia
 * la dirección (nos escribieron y contestamos, o al revés). Dos enviados seguidos con el mismo
 * asunto NO son respuesta uno del otro: el box del claim pone el número de claim como asunto por
 * defecto, así que un follow-up al ajustador y el correo al homeowner comparten asunto sin que uno
 * conteste al otro — se quedan en la misma conversación (el riel los une) pero cada uno es un
 * correo NUEVO. Si el primero que tenemos ya viene con "Re:", contesta a algo que no está en esta
 * lista (otra pestaña, otra vía): se marca como huérfano en vez de disfrazarlo de correo nuevo.
 *
 * Sin React ni fechas del sistema: todo recibe el "ahora" por parámetro para poder testearse.
 */

export type ThreadableEmail = {
  id: string;
  subject?: string | null;
  created_at: string;
  /** 'in' | 'out'. Sin dirección no hay forma de saber si el siguiente correo contesta o insiste. */
  direction?: string | null;
};

/** Prefijos de respuesta/reenvío que llegan a la oficina: Re, RE:, Fwd, FW, "Re[2]:", RV (español),
 *  AW (alemán), SV/VS (nórdicos), TR (francés). Repetidos ("Re: Re: FW:") se comen todos. */
const PREFIX_RE = /^(?:\s*(?:re|fwd?|fw|rv|aw|sv|vs|tr)\s*(?:\[\d+\])?\s*:\s*)+/i;

/** Asunto sin "Re:/Fwd:", sin espacios dobles, en minúsculas: la llave de la conversación. */
export function normalizeSubject(s: string | null | undefined): string {
  return (s ?? '').replace(PREFIX_RE, '').replace(/\s+/g, ' ').trim().toLowerCase();
}

/** ¿El asunto viene marcado como respuesta o reenvío? */
export function hasReplyPrefix(s: string | null | undefined): boolean {
  return PREFIX_RE.test(s ?? '');
}

/** Instante del correo en ms (created_at es lo que ordena el backend; se respeta). */
export function whenOf(m: { created_at: string }): number {
  const t = Date.parse(m.created_at);
  return Number.isNaN(t) ? 0 : t;
}

export type EmailThread<T> = {
  /** Asunto normalizado, o `#<id>` cuando no hay asunto (cada uno es su propia conversación). */
  key: string;
  /** El asunto tal como lo escribió quien abrió la conversación (sin "Re:"). */
  subject: string;
  /** Del más viejo al más nuevo. Quien lo pinta decide en qué orden mostrarlos. */
  messages: T[];
  rootId: string;
  firstAt: number;
  lastAt: number;
};

export type ReplyMeta<T> = {
  threadKey: string;
  /** Posición 1-based dentro de la conversación (1 = el correo que la abrió). */
  index: number;
  total: number;
  /** Respuesta = no es el primero de la conversación, o viene con "Re:" aunque sea el primero. */
  isReply: boolean;
  /** El correo al que responde (el anterior en el tiempo), si lo tenemos. */
  parent: T | null;
  /** Viene con "Re:" pero el original no está en este claim. */
  orphanReply: boolean;
};

const numericId = (id: string) => { const n = Number(id); return Number.isNaN(n) ? 0 : n; };

/** Orden cronológico estable: por fecha y, a igual fecha, por id. */
export function sortChrono<T extends ThreadableEmail>(rows: T[]): T[] {
  return [...rows].sort((a, b) => whenOf(a) - whenOf(b) || numericId(a.id) - numericId(b.id));
}

/** Marca cada correo de UNA conversación (ya en orden cronológico) como nuevo, respuesta o huérfano. */
function annotate<T extends ThreadableEmail>(th: EmailThread<T>, meta: Map<string, ReplyMeta<T>>): void {
  th.messages.forEach((m, i) => {
    const prev = i > 0 ? th.messages[i - 1]! : null;
    const prefixed = hasReplyPrefix(m.subject);
    const cambiaDireccion = Boolean(prev?.direction && m.direction && prev.direction !== m.direction);
    const isReply = prefixed || cambiaDireccion;
    meta.set(m.id, {
      threadKey: th.key, index: i + 1, total: th.messages.length,
      isReply, parent: isReply ? prev : null,
      orphanReply: i === 0 && prefixed,
    });
  });
}

/**
 * Agrupa los correos en conversaciones. Las conversaciones salen ordenadas por su ÚLTIMA
 * actividad, la más reciente primero (se abre el hilo para ver qué pasó último, no cómo empezó).
 */
export function buildThreads<T extends ThreadableEmail>(rows: T[], opts?: { flat?: boolean }): { threads: EmailThread<T>[]; meta: Map<string, ReplyMeta<T>> } {
  const chrono = sortChrono(rows);
  const byKey = new Map<string, EmailThread<T>>();
  for (const m of chrono) {
    const norm = normalizeSubject(m.subject);
    // flat: quien llama YA sabe que todo esto es una sola conversación (el lector del buzón) —
    // se anota igual (nuevo/respuesta/huérfano) sin volver a partirla por asunto.
    const key = opts?.flat ? 'flat' : (norm || `#${m.id}`);
    const at = whenOf(m);
    const th = byKey.get(key);
    if (th) { th.messages.push(m); th.lastAt = at; }
    else byKey.set(key, { key, subject: (m.subject ?? '').replace(PREFIX_RE, '').trim(), messages: [m], rootId: m.id, firstAt: at, lastAt: at });
  }
  const meta = new Map<string, ReplyMeta<T>>();
  for (const th of byKey.values()) annotate(th, meta);
  const threads = [...byKey.values()].sort((a, b) => b.lastAt - a.lastAt || numericId(b.rootId) - numericId(a.rootId));
  return { threads, meta };
}

/* ------------------------------------------------------------------ direcciones y nombres */

/** "Ana Pérez <ana@x.com>" → { name: 'Ana Pérez', email: 'ana@x.com' }; "ana@x.com" → { name: null, … }. */
export function parseAddress(raw: string | null | undefined): { name: string | null; email: string } {
  const a = (raw ?? '').trim();
  const m = a.match(/^"?([^"<]*?)"?\s*<([^>]+)>\s*$/);
  if (m) return { name: m[1]!.trim() || null, email: m[2]!.trim() };
  return { name: null, email: a };
}

/** Varias direcciones separadas por coma / punto y coma / salto de línea. */
export function splitAddresses(raw: string | null | undefined): string[] {
  return (raw ?? '').split(/[,;\n]+/).map((s) => s.trim()).filter(Boolean);
}

/** Nombre para mostrar: el nombre real si vino en la cabecera, si no la parte local del correo
 *  "en bonito" (jordan.smith → Jordan Smith). Nunca vacío. */
export function displayName(raw: string | null | undefined, fromName?: string | null): string {
  if (fromName && fromName.trim()) return fromName.trim();
  const { name, email } = parseAddress(raw);
  if (name) return name;
  const local = email.split('@')[0] || email;
  return local.replace(/[._-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) || email || '—';
}

export function initialsOf(name: string): string {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]!).join('').toUpperCase() || '?';
}

/** Matiz determinista (0–359) para el avatar de un contacto: la misma dirección, el mismo color. */
export function hueOf(s: string): number {
  let h = 0;
  for (const ch of s.toLowerCase()) h = (h * 31 + ch.charCodeAt(0)) % 360;
  return h;
}

/* ------------------------------------------------------------------ texto y tiempo */

/** Entidades HTML que aparecen en los correos reales. Las numéricas (&#39; &#8204; &#x200B;) se
 *  resuelven aparte; el resto de las nombradas raras se queda como está (mejor un "&trade;" que
 *  perder el renglón). */
const ENTITIES: Record<string, string> = {
  nbsp: ' ', amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", copy: '©', reg: '®', trade: '™',
  mdash: '—', ndash: '–', hellip: '…', rsquo: '\u2019', lsquo: '\u2018', rdquo: '\u201d', ldquo: '\u201c', bull: '•', middot: '·',
  // Ancho cero: son relleno del preheader (Allstate manda 60 seguidos), no texto.
  zwnj: '', zwj: '', shy: '',
};
const decodeEntities = (s: string) => s
  .replace(/&#x([0-9a-f]+);/gi, (_m, h: string) => String.fromCodePoint(parseInt(h, 16)))
  .replace(/&#(\d+);/g, (_m, d: string) => String.fromCodePoint(Number(d)))
  .replace(/&([a-z]+);/gi, (m, n: string) => (n.toLowerCase() in ENTITIES ? ENTITIES[n.toLowerCase()]! : m));

/** Un elemento OCULTO por su estilo en línea: el "preheader" de los correos de marketing
 *  (Allstate, JobNimbus…) y los pixeles de tracking. En el cliente de correo no se ve; en la vista
 *  previa tampoco tiene que verse. */
const HIDDEN_STYLE = /style\s*=\s*("[^"]*|'[^']*)(display\s*:\s*none|mso-hide\s*:\s*all|visibility\s*:\s*hidden|max-height\s*:\s*0(?:px)?\s*[;"']|font-size\s*:\s*0(?:px)?\s*[;"'])/i;
function dropHidden(html: string): string {
  // Se recorre etiqueta por etiqueta llevando la profundidad del elemento oculto: así un <span>
  // oculto dentro de un <div> oculto no deja escapar su cola (un regex no anidado sí lo hacía).
  const out: string[] = [];
  let hiddenTag: string | null = null; let depth = 0;
  // Tercer caso: <!DOCTYPE…>, <![endif]…> y cualquier "<…>" sin nombre de etiqueta se tira entero
  // (antes el "<!DOCTYPE html PUBLIC…" se colaba como texto y abría la vista previa).
  const re = /<\/?([a-z][a-z0-9]*)\b[^>]*>|<[^>]*>|[^<]+/gi;
  let m: RegExpExecArray | null;
  while ((m = re.exec(html))) {
    const tok = m[0];
    if (tok.startsWith('<') && !m[1]) continue;
    if (hiddenTag) {
      if (tok.startsWith('</') && m[1]!.toLowerCase() === hiddenTag) { depth--; if (depth === 0) hiddenTag = null; }
      else if (tok.startsWith('<') && !tok.startsWith('</') && !tok.endsWith('/>') && m[1]!.toLowerCase() === hiddenTag) depth++;
      continue;
    }
    if (tok.startsWith('<') && !tok.startsWith('</') && HIDDEN_STYLE.test(tok) && !/^<(img|br|input|meta|link)\b/i.test(tok)) {
      hiddenTag = m[1]!.toLowerCase(); depth = 1; continue;
    }
    out.push(tok);
  }
  return out.join('');
}

/** Una línea de vista previa del cuerpo: sin HTML, sin CSS de Outlook, sin el preheader oculto,
 *  sin caracteres de ancho cero y sin la cadena citada de abajo. */
export function snippetOf(body: string | null | undefined, max = 140): string {
  if (!body) return '';
  const sinBloques = body
    .replace(/<!--[\s\S]*?-->/g, ' ')
    .replace(/<head[\s\S]*?<\/head>/gi, ' ')
    .replace(/<style[\s\S]*?<\/style>/gi, ' ')
    .replace(/<script[\s\S]*?<\/script>/gi, ' ')
    .replace(/<title[\s\S]*?<\/title>/gi, ' ');
  const plain = decodeEntities(dropHidden(sinBloques)
    .replace(/<br\s*\/?>|<\/(?:p|div|tr|li|h[1-6]|blockquote)>/gi, '\n')
    .replace(/<\/(?:td|th)>/gi, ' ')
    // Las etiquetas EN LÍNEA (<b>, <a>…) se quitan sin dejar espacio: "<b>AllState</b>," sigue siendo "AllState,".
    .replace(/<[^>]+>/g, ''))
    // Ancho cero / marcas invisibles que sobreviven a la decodificación.
    .replace(/[\u200B-\u200D\u2060\uFEFF\u00AD]/g, '');
  // Se corta donde empieza la cadena citada: la vista previa es de ESTE correo, no del anterior.
  const lines = plain.split(/\r?\n/).map((l) => l.trim());
  const own: string[] = [];
  for (const l of lines) {
    if (/^>/.test(l) || /^On .{0,140} wrote:?$/.test(l) || /^El .{0,140} escribi(ó|o):?$/.test(l)) break;
    if (l) own.push(l);
  }
  const text = (own.length ? own : lines).join(' ').replace(/\s+/g, ' ').trim();
  return text.length > max ? `${text.slice(0, max - 1).trimEnd()}…` : text;
}

export type RelativeUnit = 'now' | 'min' | 'hour' | 'day' | 'week' | 'month' | 'year';

/** Cuánto hace, en la unidad más grande que quede entera. Quien lo pinta lo traduce. */
export function relativeParts(iso: string, now: number): { unit: RelativeUnit; n: number } {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return { unit: 'now', n: 0 };
  const s = Math.max(0, Math.round((now - t) / 1000));
  if (s < 60) return { unit: 'now', n: 0 };
  const min = Math.floor(s / 60);
  if (min < 60) return { unit: 'min', n: min };
  const h = Math.floor(min / 60);
  if (h < 24) return { unit: 'hour', n: h };
  const d = Math.floor(h / 24);
  if (d < 7) return { unit: 'day', n: d };
  if (d < 30) return { unit: 'week', n: Math.floor(d / 7) };
  if (d < 365) return { unit: 'month', n: Math.floor(d / 30) };
  return { unit: 'year', n: Math.floor(d / 365) };
}

/** Los dos instantes caen en el mismo día de la OFICINA (hora del Este)? Para decidir si al
 *  "Abierto" se le pone solo la hora o también la fecha. */
export function sameOfficeDay(a: string, b: string): boolean {
  const f = (iso: string) => new Date(iso).toLocaleDateString(localeUI(), { timeZone: 'America/New_York' });
  return f(a) === f(b);
}

/**
 * LA RUTA PARA RESPONDER UN CORREO DESDE CUALQUIER PANTALLA (dueño 2026-10-19: "donde me muestre un
 * email, tengo un botón de reply"). Las tarjetas sueltas (historial, chat, timeline, HO communication)
 * no tienen compositor: mandan al tab Email del claim con `?reply=<id>`, y el tab abre la respuesta.
 */
export function rutaParaResponder(claimId: string, m: { id: string; recipient_type?: string | null }): string {
  const cat = m.recipient_type && ['insurance', 'contractor', 'homeowner'].includes(m.recipient_type) ? m.recipient_type : null;
  return `/claims/${claimId}?tab=email${cat ? `&thread=${cat}` : ''}&reply=${encodeURIComponent(m.id)}`;
}

/** "Re: asunto" sin apilar prefijos (Re: Re: …). */
export function asuntoDeRespuesta(subject: string | null | undefined): string {
  return `Re: ${(subject ?? '').replace(/^(\s*(re|fwd|fw)\s*:\s*)+/i, '').trim()}`.trim();
}

/** A quién va la respuesta: a quien escribió (entrante) o a quien se le escribió (saliente). */
export function destinoDeRespuesta(m: { direction: string; from_address?: string | null; to_address?: string | null }): string {
  return (m.direction === 'out' ? m.to_address : m.from_address) ?? '';
}
